/**
 * @file: useServiceRecords.ts
 * @copyright: (c) 2019-2021 sichuan zhichetech co., ltd.
 */
/**
 * @file: useHistoryTasks.ts
 * @copyright: (c) 2019-2021 sichuan zhichetech co., ltd.
 */

import { useCurrentUserDangerously } from '@euler/app/flows/auth/context';
import { useInfiniteList } from '@euler/functions/useInfiniteList';
import { ResourceAccessScope } from '@euler/model/enum';
import { taskListItemFromServiceRecord } from '@euler/model/util';
import { useServiceFactory } from '@euler/services/factory';
import { delayed, onErrorIgnore } from '@euler/utils';
import { useCallback, useEffect } from 'react';

export function useDashboardServiceRecords({
  tag,
  assignedToMe,
  limit,
}: {
  tag?: string;
  assignedToMe?: boolean;
  limit: number;
}) {
  const { serviceRecordService } = useServiceFactory();
  const { user } = useCurrentUserDangerously();
  const assignedTo = assignedToMe && user.memberId ? user.memberId : undefined;

  const query = useCallback(
    async (offset: number) => {
      return await serviceRecordService
        .search(
          {
            authorId: assignedTo,
            scope: ResourceAccessScope.Store,
            tag,
          },
          null,
          offset,
          limit,
        )
        .then(tasks => tasks.map(taskListItemFromServiceRecord));
    },
    [serviceRecordService, assignedTo, tag, limit],
  );

  const {
    fetch,
    fetchWithoutRefresh,
    fetchMore,
    fetchSinceLastError,
    update,
    ...list
  } = useInfiniteList({
    limit,
    query,
    initialLoading: true,
  });

  const onScrollToBottom = useCallback(() => {
    fetchMore().catch(onErrorIgnore);
  }, [fetchMore]);

  useEffect(() => {
    return delayed(() => {
      fetch().catch(onErrorIgnore);
    }, 100);
  }, [fetch]);

  return {
    onScrollToBottom,
    fetch,
    fetchWithoutRefresh,
    fetchMore,
    fetchSinceLastError,
    update,
    ...list,
  };
}
